import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { observer } from 'src/utils/mobx-react';
import { useAppStateContext } from 'src/AppState/AppState';
import Tabs from 'src/components/Tabs';
import { AffordabilityAuditContent } from 'src/components/AuditAffordability/AuditAffordability';
import { ComplianceAuditContent } from 'src/components/ComplianceAudit/ComplianceAudit';
import { ComplianceAuditsState, isComplianceAuditTabId } from './ComplianceAudits.state';
import type { ComplianceAuditTabIdType } from './ComplianceAudits.state';

const TAB_LABELS: Record<ComplianceAuditTabIdType, string> = {
    affordabilityAudit: 'Affordability Audit',
    selfExclusionAudit: 'Self Exclusion Audit',
    gamstopAudit: 'Gamstop Audit',
};

const TabContent = ({ tabId }: { tabId: ComplianceAuditTabIdType; label: string }): React.ReactElement | null => {
    switch (tabId) {
        case 'affordabilityAudit':
            return <AffordabilityAuditContent />;
        case 'selfExclusionAudit':
            return <ComplianceAuditContent feature='SelfExclusion' />;
        case 'gamstopAudit':
            return <ComplianceAuditContent feature='Gamstop' />;
        default:
            return null;
    }
};

export const ComplianceAudits = observer('ComplianceAudits', () => {
    const { routerBackoffice } = useAppStateContext();
    const [complianceAuditsState] = useState(() => new ComplianceAuditsState(routerBackoffice));

    useEffect(() => {
        return (): void => complianceAuditsState.dispose();
    }, [complianceAuditsState]);

    const { isVisible, orderedTabs, activeTab } = complianceAuditsState;

    const handleTabChange = (key: number | string | null): void => {
        if (typeof key === 'string' && isComplianceAuditTabId(key)) {
            complianceAuditsState.switchTab(key);
        }
    };

    const handleTabClose = (key: number | string | null): void => {
        if (typeof key === 'string' && isComplianceAuditTabId(key)) {
            complianceAuditsState.closeTab(key);
        }
    };

    return (
        <div className={classNames(['audits', { visible: isVisible }])}>
            <div className='audits-content'>
                <button
                    className='audits-minimize'
                    onClick={complianceAuditsState.minimize}
                />
                <Tabs
                    className='tabs__vertical tabs__audits'
                    onClose={handleTabClose}
                    allowClose={true}
                    onChange={handleTabChange}
                    active={activeTab}
                    tabsPlacement='audit-tabs'
                    keepMounted
                >
                    {orderedTabs.map((tabId) => (
                        <TabContent
                            key={tabId}
                            tabId={tabId}
                            label={TAB_LABELS[tabId]}
                        />
                    ))}
                </Tabs>
            </div>
        </div>
    );
});
